import React from 'react';

const Documentations = () => {
  return (
    <div className="container mx-auto p-8 bg-gray-100 rounded-lg shadow-md mt-8">
      <h1 className="text-3xl font-bold mb-6">Data Preprocessing Techniques</h1>

      {/* Min-Max Scaling */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">1. Min-Max Scaling</h2>
        <p>
          Min-max scaling transforms features by scaling them to a range between 0 and 1. It's calculated using the formula:
          <br />
          {/* <span className="italic">
            {' '}
            \[ X_{scaled} = \frac{X - X_{min}}{X_{max} - X_{min}} \]
          </span> */}
        </p>
        <p className="mt-2">
          This technique is useful when you want to preserve the shape of the original distribution but normalize the range
          of features.
        </p>
      </div>

      {/* Robust Scaling */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">2. Robust Scaling</h2>
        <p>
          Robust scaling scales features using statistics that are robust to outliers. It's calculated using the formula:
          <br />
          {/* <span className="italic">
            {' '}
            \[ X_{scaled} = \frac{X - \text{median}(X)}{\text{IQR}} \]
          </span> */}
        </p>
        <p className="mt-2">
          This technique is suitable when your data contains outliers and you want to scale features without being affected
          by them.
        </p>
      </div>

      {/* Standard Scaling */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">3. Standard Scaling (Standardization)</h2>
        <p>
          Standard scaling standardizes features by removing the mean and scaling to unit variance. It's calculated using the
          formula:
          <br />
          {/* <span className="italic">
            {' '}
            \[ X_{scaled} = \frac{X - \text{mean}(X)}{\text{std}(X)} \]
          </span> */}
        </p>
        <p className="mt-2">
          This technique is useful when the features have different scales and you want to center them around 0 with a standard
          deviation of 1.
        </p>
      </div>

      {/* Imputation */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">4. Imputation</h2>
        <p>
          Imputation is the process of filling in missing values in the dataset. Two common methods are:
          <br />
          - <span className="italic">Mean Imputation:</span> Replace missing values with the mean of the feature.
          <br />
          - <span className="italic">Median Imputation:</span> Replace missing values with the median of the feature.
        </p>
      </div>

      {/* Binning */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">5. Binning</h2>
        <p>
          Binning is the process of grouping numerical data into bins or intervals. It's useful for converting continuous data
          into categorical data.
          <br />
          Binning can be done using various methods such as equal-width binning and equal-frequency binning.
        </p>
      </div>

      {/* Forward Fill and Backward Fill */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">6. Forward Fill (ffill) and Backward Fill (bfill)</h2>
        <p>
          - Forward fill (ffill) replaces missing values with the last known value in the dataset.
          <br />
          - Backward fill (bfill) replaces missing values with the next known value in the dataset.
          <br />
          These methods are commonly used for time series data where missing values can be filled based on the previous or next
          observations.
        </p>
      </div>

      {/* Outlier Handling */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">7. Outlier Handling</h2>
        <p>
          Outliers are data points that significantly differ from other observations in the dataset.
          <br />
          Techniques for handling outliers include:
          <br />
          - <span className="italic">Trimming:</span> Removing outliers from the dataset.
          <br />
          - <span className="italic">Capping:</span> Capping or clipping extreme values to a predefined threshold.
          <br />
          - <span className="italic">Change by Mean:</span> Replacing outlier values with the mean of the feature.
        </p>
      </div>

      <p className="mt-8">
        Including these explanations in your API documentation will help users understand the purpose and usage of each
        preprocessing technique.
      </p>
    </div>
  );
};

export default Documentations;
